/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { withRouter } from 'react-router-dom';
import { EmptyState, WidgetV2 } from '../../../../components/v2';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { renderStatus } from 'components/v2/cell-renderers/status';
import { statusDescription } from '../../../../constants/status';
import { getDigitacaoAntecipadaOverlay } from '../../../../helpers/digitacaoAntecipada';
import {
  buildLocalLabel,
  FILA_SORT_MODES,
  FILA_SORT_OPTIONS,
  getFilaItemCta,
  sortFilaItens,
} from './constants';
import DigitacaoSortMenuButton from './DigitacaoSortMenuButton';
import FilaNsirMetaChips from './FilaNsirMetaChips';
import TipoServicoChips from './TipoServicoChips';

const NsirsAtivasCards = ({ itens, loading, history, readOnly }) => {
  const [sortMode, setSortMode] = useState(FILA_SORT_MODES.STATUS);

  const sortedItens = useMemo(
    () => sortFilaItens(itens, sortMode),
    [itens, sortMode],
  );

  return (
    <WidgetV2
      title={readOnly ? 'Serviços do digitador' : 'Meus serviços'}
      initialExpanded
      actions={[
        {
          isComponent: true,
          component: (
            <DigitacaoSortMenuButton
              sortMode={sortMode}
              onChange={setSortMode}
              options={FILA_SORT_OPTIONS}
            />
          ),
        },
      ]}
    >
      <Box id="minha-fila" sx={{ p: 1.5 }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Carregando serviços…
          </Typography>
        ) : !sortedItens?.length ? (
          <EmptyState
            icon={<AssignmentOutlinedIcon />}
            title="Nenhum serviço atribuído"
            message="Serviços aparecem aqui quando uma NSIR for vinculada a você."
          />
        ) : (
          <Stack spacing={1}>
            {sortedItens.map((item) => {
              const digitados = item.digitados ?? 0;
              const total = item.totalServicos ?? item.total ?? 0;
              const pendentesCount = Math.max(0, total - digitados);
              const pct = total > 0 ? Math.round((digitados / total) * 100) : 0;
              const overlay = getDigitacaoAntecipadaOverlay(item);
              const cta = getFilaItemCta(item, { readOnly });
              const local = buildLocalLabel(item);
              const nsirHref = `/v2/nsir/${item.nsirId}`;
              const openInNewTab = !readOnly && cta.label === 'Continuar';

              return (
                <Box
                  key={item.nsirId}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    justifyContent="space-between"
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        noWrap
                        title={item.nsirCodigo}
                      >
                        NSIR {item.nsirCodigo}
                      </Typography>
                      {item.status && (
                        <Box sx={{ display: 'inline-flex' }}>
                          {renderStatus({
                            value: item.status,
                            label: statusDescription(item.status) || item.status,
                          })}
                        </Box>
                      )}
                      {overlay && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label={overlay.label}
                        />
                      )}
                      <TipoServicoChips item={item} />
                    </Stack>
                    <Button
                      size="small"
                      variant={cta.variant}
                      {...(openInNewTab
                        ? {
                            component: 'a',
                            href: `${window.location.origin}${nsirHref}`,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                          }
                        : {
                            onClick: () => history.push(nsirHref),
                          })}
                      sx={{ flexShrink: 0, mt: -0.25 }}
                    >
                      {cta.label}
                    </Button>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <FilaNsirMetaChips item={item} />
                  </Stack>
                  {local && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                      title={local}
                      sx={{ mt: 0.5 }}
                    >
                      {local}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                    <Typography variant="caption" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {digitados}/{total} · {pct}%
                      {pendentesCount > 0 ? ` · ${pendentesCount} pendentes` : ''}
                      {digitados >= total && total > 0 ? ' ✓' : ''}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{ flex: 1, height: 4, borderRadius: 1 }}
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </WidgetV2>
  );
};

NsirsAtivasCards.propTypes = {
  itens: PropTypes.array,
  loading: PropTypes.bool,
  history: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
};

NsirsAtivasCards.defaultProps = {
  itens: [],
  loading: false,
  readOnly: false,
};

export default withRouter(NsirsAtivasCards);
